import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { formatDistanceToNow } from "date-fns";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { EmptyState } from "@/components/ui";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import type { DiscoveryPromptDoneIntent, TestAgentV2Session } from "../types/test-agent-2.ts";
import type { OrgSettings } from "../types.ts";
import { markdownToPlainText } from "../utils/text.ts";
import { DataGrid } from "./data-grid.tsx";
import { GenerateFastStatusChip } from "./generate-fast-status-chip.tsx";

type Props = {
  open: boolean;
  onClose: () => void;
};

const autoDiscoveryColumns: GridColDef<TestAgentV2Session>[] = [
  {
    field: "intent",
    headerName: "Intent",
    flex: 1,
    valueGetter: (_, row: TestAgentV2Session) => {
      const lastDone = row.data?.last_done as DiscoveryPromptDoneIntent | null;
      const intent = markdownToPlainText(lastDone?.intent?.slice(0, 100) ?? "N/A");

      if (intent.length > 60) {
        return `${intent.slice(0, 57)}...`;
      }

      return intent;
    },
  },
  {
    field: "test_count",
    headerName: "Tests",
    width: 60,
    valueGetter: (_, row: TestAgentV2Session) => {
      const lastDone = row.data?.last_done as DiscoveryPromptDoneIntent | null;
      return lastDone?.test_count || 0;
    },
  },
  {
    field: "status",
    headerName: "Status",
    width: 120,
    renderCell: (params: GridRenderCellParams<TestAgentV2Session>) => {
      const status = params.row.data?.status || "unknown";
      return <GenerateFastStatusChip status={status} />;
    },
  },
  {
    field: "created_at",
    headerName: "Created",
    width: 160,
    valueGetter: (value: string) => {
      return formatDistanceToNow(new Date(value), { addSuffix: true });
    },
  },
];

export const AutoDiscoveriesModal = ({ open, onClose }: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const nav = useNavigate();

  // Fetch org settings
  const {
    data: orgSettings,
    isLoading: isLoadingSettings,
    refetch: refetchSettings,
  } = useApiQuery<OrgSettings>("/orgs/settings", {
    enabled: open,
  });

  // Fetch auto-discoveries
  const {
    data: autoDiscoveries,
    isLoading: isLoadingDiscoveries,
    refetch: refetchDiscoveries,
  } = useApiQuery<TestAgentV2Session[]>("/auto-discoveries", {
    enabled: open,
  });

  // Mutation for updating org settings
  const { mutate: updateSettings, isPending: isUpdatingSettings } = useApiMutation<
    OrgSettings,
    OrgSettings
  >("PATCH", "/orgs/settings", {
    onSuccess: () => {
      enqueueSnackbar("Settings updated successfully", {
        variant: "success",
      });
      refetchSettings();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update settings: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Mutation for triggering auto-discovery
  const { mutate: triggerDiscovery, isPending: isTriggering } = useApiMutation<
    void,
    { status: string; agent_id: string; message: string }
  >("POST", "/auto-discoveries/trigger", {
    onSuccess: (data) => {
      enqueueSnackbar(data.message, {
        variant: "success",
      });
      refetchDiscoveries();
      // No redirect - stay in modal
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to trigger auto-discovery: ${error.message}`, {
        variant: "error",
      });
    },
  });

  const handleToggle = (enabled: boolean) => {
    updateSettings({ weekly_discovery_enabled: enabled });
  };

  const handleTrigger = () => {
    triggerDiscovery({});
  };

  const isEnabled = orgSettings?.weekly_discovery_enabled ?? false;

  const sortedDiscoveries = useMemo(() => {
    return [...(autoDiscoveries || [])]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 5); // Limit to last 5 auto-discoveries
  }, [autoDiscoveries]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Auto-Discoveries</DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          {/* Enable/Disable Toggle */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack>
                <Typography variant="subtitle1" fontWeight="600">
                  Weekly Auto-Discovery
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically discover new test scenarios every week
                </Typography>
              </Stack>
              {isLoadingSettings ? (
                <CircularProgress size={24} />
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(e) => handleToggle(e.target.checked)}
                      disabled={isUpdatingSettings}
                    />
                  }
                  label={isEnabled ? "Enabled" : "Disabled"}
                />
              )}
            </Stack>
          </Box>

          {/* Previous Auto-Discoveries */}
          <Box>
            <Typography variant="subtitle1" fontWeight="600" mb={1}>
              Previous Auto-Discoveries
            </Typography>
            {isLoadingDiscoveries ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : sortedDiscoveries.length > 0 ? (
              <DataGrid
                rows={sortedDiscoveries}
                columns={autoDiscoveryColumns}
                minHeight="220px"
                hideFooter
                onRowClick={(params) => {
                  nav(`/discovery-review/${params.row.id}`);
                  onClose();
                }}
                containerSx={{
                  "& .MuiDataGrid-row": {
                    cursor: "pointer",
                  },
                  "& .MuiDataGrid-cell": {
                    py: 1,
                  },
                }}
              />
            ) : (
              <EmptyState message="No auto-discoveries yet. Enable weekly auto-discovery or trigger one manually." />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleTrigger}
          disabled={!isEnabled || isTriggering}
          startIcon={isTriggering ? <CircularProgress size={16} /> : null}
        >
          {isTriggering ? "Triggering..." : "Trigger New Discovery"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoDiscoveriesModal;
